import { useHideFloatingButtons } from "@/components/support/useFloatingButtonsLift";
import { Drawer } from "@medusajs/ui";
import type { ReactNode } from "react";

/**
 * The app's default off-canvas primitive, re-exported from `@medusajs/ui` so
 * the drawer surfaces - filters, the phone search overlay, the cart slide-over
 * - are imported from one place and styled the same. Reach for the low-level
 * compound (`Drawer.Content`, `Drawer.Trigger`, ...) when a surface needs more
 * than the {@link DrawerPanel} shape below.
 *
 * The one exception is a drawer that dialogs from `./dialog` open on top of
 * (the order assistant, whose cards open the product dialog). `Drawer` carries
 * its own copy of Radix, which does not nest with the app's dialogs: use
 * `NestableDrawer` (`./nestable-drawer`) for that, and `Drawer` for the rest.
 *
 * Note for future consumers: `Drawer.Content` is a Radix portal, so it mounts
 * outside the element it's written inside. A parent's `PII_MASK_CLASS` does NOT
 * reach it (see `CLAUDE.md` → session-replay). A drawer that renders salon data
 * must carry the mask class on its own content.
 */
export { Drawer };

export interface DrawerPanelProps {
  /** Whether the drawer is open. Controlled - pair with `onOpenChange`. */
  open: boolean;
  /** Called when the drawer requests open/close (overlay click, Esc, close). */
  onOpenChange: (open: boolean) => void;
  /**
   * Accessible title, shown in the header. Required: Radix's dialog warns
   * without one, and a screen reader announces it as the surface opens.
   */
  title: string;
  /** Optional supporting line under the title. */
  description?: string;
  /** The scrollable body content. */
  children: ReactNode;
  /** Optional pinned footer, e.g. primary actions. */
  footer?: ReactNode;
  /**
   * Overrides for the content panel. Passed to `@medusajs/ui`'s `Drawer.Content`,
   * which defaults to a right-anchored panel capped at 560px.
   *
   * Mind the merge: medusa hard-codes its positioning with responsive variants
   * (`sm:right-2 sm:max-w-[560px]`, `max-sm:inset-x-2 max-sm:w-[calc(100%-16px)]`),
   * and `tailwind-merge` only replaces a class when the variant prefix matches.
   * So an override must carry the same prefix - `sm:max-w-[720px]` widens the
   * panel, whereas a bare `max-w-full` is silently ignored at `sm`+. A
   * full-screen overlay therefore has to override each of medusa's `sm:`/`max-sm:`
   * positioning utilities, not just the base ones.
   */
  contentClassName?: string;
}

/**
 * The common drawer shape - controlled open state, a titled header (with the
 * built-in close affordance), a scrolling body, and an optional pinned footer -
 * so surfaces don't each re-wire the Radix compound. When a surface needs
 * something this doesn't express, compose {@link Drawer} directly instead.
 */
export function DrawerPanel({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  contentClassName
}: DrawerPanelProps) {
  // The panel is inset from the viewport edge (`max-sm:inset-x-2`), so the
  // phone cart bar would otherwise show in the margin around it (KAN-410).
  useHideFloatingButtons(open);
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <Drawer.Content
        className={contentClassName}
        // Opt out of Radix's "missing description" warning when the caller
        // gives none; when they do, Drawer.Description wires the link itself.
        {...(description ? {} : { "aria-describedby": undefined })}
      >
        <Drawer.Header>
          <Drawer.Title>{title}</Drawer.Title>
          {description ? (
            <Drawer.Description>{description}</Drawer.Description>
          ) : null}
        </Drawer.Header>
        <Drawer.Body className="min-h-0 overflow-y-auto">
          {children}
        </Drawer.Body>
        {footer ? <Drawer.Footer>{footer}</Drawer.Footer> : null}
      </Drawer.Content>
    </Drawer>
  );
}
